import {FC, MouseEvent, memo, useContext, useEffect, useState} from 'react';
import {useRecoilValue, useSetRecoilState} from 'recoil';
import dayjs from 'dayjs';

import TableRow from '@mui/material/TableRow';
import IconButton from '@mui/material/IconButton';
import Box from '@mui/material/Box';
import TableCell, {TableCellProps} from '@mui/material/TableCell';
import Collapse from '@mui/material/Collapse';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import {Checkbox, Divider, FormControlLabel, Stack, Table, TableBody, Tooltip, Typography, styled} from '@mui/material';
import Status from '../../../../ui/status/Status';

import {IServiceRequestImports} from '../../../../shared/types/serviceRequestImport.types';

import {colors} from '../../../../shared/colors';
import {grey, blue} from '@mui/material/colors';

import {DocumentService} from '../../../../api/services/document.service';

import {openedImportRowsState} from '../../../../store/service-request-imports/atoms';
import {isImportRowOpenedFamily} from '../../../../store/service-request-imports/selectors';

import {EventBusContext} from '../../../../providers/EventBus';
import {getFullName} from '../../../../shared/helpers/getFullName';
import {DATETIME_FORMAT} from '../../../../shared/constants';

interface IImportRow {
  row: IServiceRequestImports;
  index: number;
}

const successImportRowCountStyle = {
  color: colors.green,
};

const updatedImportRowCountStyle = {
  color: blue[700],
};

const addedImportRowCountStyle = {
  color: colors.green,
};

const skippedImportRowCountStyle = {
  color: grey[700],
};

const errorImportRowCountStyle = {
  color: colors.red,
};

const fontStyle = {
  fontSize: '13px',
};

const BorderlessTableCell = styled(TableCell)<TableCellProps>(({theme}) => ({
  '.MuiTableCell-root:not(.MuiTableCell-root + .MuiTableRow-root)': {
    borderBottom: 0,
  },
}));

const CustomToast: FC<{rowId: string; message: string}> = ({rowId, message}) => {
  const [checked, setChecked] = useState(false);

  const clickHandler = () => {
    const stopNotifying = localStorage.getItem('stopNotifying');

    const stopNotifyingArr: string[] = stopNotifying ? JSON.parse(stopNotifying) : [];

    !checked &&
      localStorage.setItem(
        'stopNotifying',
        JSON.stringify(stopNotifyingArr.length ? [rowId, ...stopNotifyingArr] : [rowId]),
      );

    setChecked(!checked);
  };

  return (
    <Stack direction="column">
      <Typography>{message}</Typography>
      <Stack direction="row">
        <FormControlLabel
          control={<Checkbox checked={checked} onChange={clickHandler} />}
          label="Больше не показывать"
        />
      </Stack>
    </Stack>
  );
};

export const ImportRow: FC<IImportRow> = memo(({row, index}) => {
  const setOpen = useSetRecoilState(openedImportRowsState);
  const isOpened = useRecoilValue(isImportRowOpenedFamily(row.id));
  const {bus} = useContext(EventBusContext);

  const hasNoTemplate = row.errorCode === 'TEMPLATE_NOT_FOUND';

  const toggleCollapse = (mode: 'open' | 'close') => (e: MouseEvent<HTMLButtonElement | HTMLTableRowElement>) => {
    e.stopPropagation();

    mode === 'open' ? setOpen((prev) => [row.id, ...prev]) : setOpen((prev) => prev.filter((item) => item !== row.id));
  };

  const onDownLoad = (docId: string) => async (e: MouseEvent<unknown>) => {
    e.preventDefault();
    e.stopPropagation();
    try {
      const res = await DocumentService.getContent(String(docId));

      const url = URL.createObjectURL(res.data);

      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = (
        row.sourceDocumentId === docId ? row.sourceDocument?.fileName : row.errorDocument?.fileName
      ) as string;
      anchor.style.display = 'none';
      document.body.append(anchor);
      anchor.click();
      anchor.remove();
    } catch (err: any) {
      bus?.emit('error', [err]);
    }
  };

  useEffect(() => {
    const stopNotifying = localStorage.getItem('stopNotifying');

    if (hasNoTemplate) {
      const stopNotifyingArr: string[] = stopNotifying ? JSON.parse(stopNotifying) : [];

      if (!stopNotifyingArr.some((i) => i === row.id)) {
        bus?.emit('error', [
          <CustomToast
            rowId={row.id}
            message={`Для файла "${row.sourceDocument?.fileName}" (строка ${index}) шаблон импорта не найден`}
          />,
          {
            autoClose: 30000,
            toastId: row.id,
          },
        ]);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasNoTemplate]);

  return (
    <>
      <TableRow
        onClick={toggleCollapse(isOpened ? 'close' : 'open')}
        hover
        sx={{cursor: 'pointer'}}
        selected={isOpened}
      >
        <BorderlessTableCell sx={{width: '34px'}}>
          <IconButton aria-label="expand row" size="small" onClick={toggleCollapse(isOpened ? 'close' : 'open')}>
            {isOpened ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </BorderlessTableCell>
        <BorderlessTableCell scope="row" align="center">
          {index}
        </BorderlessTableCell>
        <BorderlessTableCell align="left" sx={{maxWidth: '16rem'}}>
          <Box sx={{maxWidth: '16rem', overflow: 'hidden', textOverflow: 'ellipsis', display: 'inline-block'}}>
            <Tooltip title={row.sourceDocument?.fileName}>
              <Typography
                sx={{color: colors['icons--primary'], textDecoration: 'underline', cursor: 'pointer'}}
                onClick={onDownLoad(row.sourceDocumentId as string)}
                textOverflow="ellipsis"
                noWrap
                variant="subtitle2"
              >
                {row.sourceDocument?.fileName}
              </Typography>
            </Tooltip>
          </Box>
        </BorderlessTableCell>
        <BorderlessTableCell align="center">
          <Typography sx={fontStyle} variant="subtitle2" fontWeight={400}>
            {(row.importStartDate && dayjs(row.importStartDate).format(DATETIME_FORMAT)) || '-'}
          </Typography>
        </BorderlessTableCell>
        <BorderlessTableCell align="center">
          <Typography sx={fontStyle} variant="subtitle2" fontWeight={400}>
            {(row.importFinishDate && dayjs(row.importFinishDate).format(DATETIME_FORMAT)) || '-'}
          </Typography>
        </BorderlessTableCell>
        <BorderlessTableCell align="center">
          <Typography sx={fontStyle} variant="subtitle2">
            {row.initiator ? getFullName(row.initiator) : '-'}
          </Typography>
        </BorderlessTableCell>
        <BorderlessTableCell align="center">
          {row.statusCode ? (
            <Status sx={{fontSize: '12px'}} variant="subtitle2" entity="serviceRequestImport" code={row.statusCode} />
          ) : (
            '-'
          )}
        </BorderlessTableCell>
      </TableRow>
      <TableRow sx={{backgroundColor: colors['bg--on-white']}}>
        <BorderlessTableCell style={{paddingBottom: 0, paddingTop: 0}} colSpan={8}>
          <Collapse in={isOpened} timeout="auto" unmountOnExit>
            <Box sx={{margin: 1}}>
              {hasNoTemplate ? (
                <Stack justifyContent="center" direction="row">
                  <Typography variant="subtitle2">Шаблон для импорта не найден</Typography>
                </Stack>
              ) : (
                <Table size="small">
                  <TableBody>
                    <TableRow>
                      <BorderlessTableCell padding="none">Записей всего</BorderlessTableCell>
                      <BorderlessTableCell padding="none">
                        <Typography sx={successImportRowCountStyle}>{row.totalRowCount || 0}</Typography>
                      </BorderlessTableCell>
                      <BorderlessTableCell padding="none">
                        <Stack direction="column" spacing={0.5}>
                          <Typography variant="subtitle2">Обновлено</Typography>
                          <Typography variant="subtitle2">Добавлено</Typography>
                          <Typography variant="subtitle2">Пропущено</Typography>
                        </Stack>
                      </BorderlessTableCell>
                      <BorderlessTableCell padding="none">
                        <Stack paddingX={1} direction="column" spacing={0.5}>
                          <Typography variant="subtitle2" sx={updatedImportRowCountStyle}>
                            {row.updateRowCount || 0}
                          </Typography>
                          <Typography variant="subtitle2" sx={addedImportRowCountStyle}>
                            {row.addRowCount || 0}
                          </Typography>
                          <Typography variant="subtitle2" sx={skippedImportRowCountStyle}>
                            {row.skipRowCount || 0}
                          </Typography>
                        </Stack>
                      </BorderlessTableCell>
                      <BorderlessTableCell padding="none">С ошибками</BorderlessTableCell>
                      <BorderlessTableCell padding="none">
                        <Typography sx={errorImportRowCountStyle}>{row.failedRowCount || 0}</Typography>
                      </BorderlessTableCell>
                      <BorderlessTableCell
                        padding="none"
                        align="left"
                        sx={{maxWidth: '10rem', overflow: 'hidden', textOverflow: 'ellipsis'}}
                      >
                        {row.errorDocument && (
                          <Tooltip title={row.errorDocument.fileName}>
                            <Typography
                              sx={{color: colors['icons--primary'], textDecoration: 'underline', cursor: 'pointer'}}
                              onClick={onDownLoad(row.errorDocumentId as string)}
                              textOverflow="ellipsis"
                              noWrap
                              variant="subtitle2"
                            >
                              {row.errorDocument.fileName}
                            </Typography>
                          </Tooltip>
                        )}
                      </BorderlessTableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              )}
              {row.description && (
                <>
                  <Divider />
                  <Stack
                    direction="row"
                    alignItems="center"
                    sx={{
                      margin: 1,
                    }}
                  >
                    <Typography component="div" marginRight={2} variant="subtitle2" fontWeight={500}>
                      {'Комментарий: '}
                    </Typography>
                    <Typography
                      component="div"
                      sx={{wordBreak: 'break-all'}}
                      flexWrap="wrap"
                      variant="subtitle2"
                      fontWeight={400}
                    >
                      {row.description}
                    </Typography>
                  </Stack>
                </>
              )}
            </Box>
          </Collapse>
        </BorderlessTableCell>
      </TableRow>
    </>
  );
});
